import { Fragment, useState } from 'react';

import { Button } from '@/components/ui/button';

import type { IInvitationDetails } from '../model';

import { ConfirmInvitationAlertDialog } from './ConfirmInvitationAlertDialog';
import { EscrowInvitationDialog } from './EscrowInvitationDialog';
import { InvitationDetails } from './InvitationDetails';
import { InvitationDialogLayout } from './InvitationDialogLayout';

type Props = {
  details: IInvitationDetails;
  onAccept: () => void;
  onReject: () => void;
  onClose: () => void;
  acceptLoading?: boolean;
  rejectLoading?: boolean;
};

export function InvitationPending({
  details,
  onAccept,
  onReject,
  onClose,
  acceptLoading = false,
  rejectLoading = false,
}: Props) {
  const { appName, requester } = details;
  const [confirmAcceptOpen, setConfirmAcceptOpen] = useState(false);
  const [confirmDeclineOpen, setConfirmDeclineOpen] = useState(false);

  const handleAcceptClick = () => setConfirmAcceptOpen(true);
  const handleRejectClick = () => setConfirmDeclineOpen(true);

  const confirming = confirmAcceptOpen || confirmDeclineOpen;

  return (
    <Fragment>
      <EscrowInvitationDialog open={!confirming} onClose={onClose}>
        <InvitationDialogLayout
          title="You've been invited as an escrow member"
          description={`You have been invited to join an escrow${requester ? ` with ${requester.name}` : ''} on ${appName}. Accept or reject the invitation to let the other party know how to proceed.`}
          footer={
            <Fragment>
              <Button variant="destructive" onClick={handleRejectClick}>
                Reject invite
              </Button>
              <Button onClick={handleAcceptClick}>Accept invite</Button>
            </Fragment>
          }
        >
          <InvitationDetails details={details} />
        </InvitationDialogLayout>
      </EscrowInvitationDialog>
      <ConfirmInvitationAlertDialog
        open={confirmAcceptOpen}
        onOpenChange={setConfirmAcceptOpen}
        title="Accept this invitation?"
        description={`We'll take you through setup next. Once you're done, ${appName} appears in your applications, where you can see the agreement and its status.`}
        confirmLabel="Accept and continue"
        onConfirm={onAccept}
        loading={acceptLoading}
      />
      <ConfirmInvitationAlertDialog
        open={confirmDeclineOpen}
        onOpenChange={setConfirmDeclineOpen}
        title="Decline this invitation?"
        description={`The escrow won't be created. If you change your mind later, ${requester?.name ?? 'the other party'} will have to set it up and invite you again.`}
        confirmLabel="Decline invitation"
        confirmVariant="destructive"
        onConfirm={onReject}
        loading={rejectLoading}
      />
    </Fragment>
  );
}
